import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classes from './PromoCodes.module.css';
import { AppDispatch, RootState } from '../../../store/store';
import Card from '../../UI/Card/Card';
import IconButton from '../../UI/IconButton/IconButton';
import AddIcon from '../../UI/icons/AddIcon/AddIcon';
import Placeholder from '../../UI/Placeholder/Placeholder';
import AreaLoader from '../../UI/AreaLoader/AreaLoader';
import PromoCodesList from '../PromoCodesList/PromoCodesList';
import PromoCodeForm from '../PromoCodeForm/PromoCodeForm';
import useForm from '../../../hooks/useForm';
import { createPromoCode, deletePromoCode, fetchPromoCodes, removeSelectedPromoCode, selectPromoCode, updatePromoCodeStatus } from '../../../store/PromoCodeSlice';
import { promoCodeFormValidator } from '../../../utils/validators';
import { PromoCode, AlertType } from '../../../types/common';
import { showAlert } from '../../../store/CommonSlice';
import { DELETE_PROMOCODE_ALERT_MESSAGE, NO_PROMOCODES_MESSAGE } from '../../../constants/messages';

const INIT_INPUT: Partial<PromoCode> = {
    code: '',
    discountType: 'percentage',
    discountValue: 1,
    type: 'global',
    usageLimit: 1,
    status: 'active',
};

const PromoCodes: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { promoCodes, isLoading, error, selectedPromoCode } = useSelector((s: RootState) => s.promocode);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const { input, setInput, handleChange: handleInputChange, errors, submit, resetForm } = useForm(INIT_INPUT, handleSubmit, promoCodeFormValidator);

    function handleSelect(option: { [key: string]: string }) {
        const { field, id } = option;
        if (!field) return;
        setInput((prev) => {
            const next = { ...prev, [field]: id || '' };
            if (field === 'type') {
                if (id === 'personal') {
                    delete (next as any).usageLimit;
                } else {
                    delete (next as any).phone;
                }
            }
            return next;
        });
    }

    useEffect(() => {
        dispatch(fetchPromoCodes());
    }, [dispatch]);

    useEffect(() => {
        if (!selectedPromoCode.id) return;
        setInput((prev) => ({ ...prev, ...selectedPromoCode }));
    }, [selectedPromoCode, setInput]);

    function openForm() {
        setIsFormOpen(true);
    }

    function closeForm() {
        setIsFormOpen(false);
        dispatch(removeSelectedPromoCode());
        resetForm();
    }

    function handleSubmit() {
        if (selectedPromoCode.id) {
            dispatch(
                updatePromoCodeStatus({
                    id: selectedPromoCode.id,
                    status: input.status as PromoCode['status'],
                }),
            );
        } else {
            dispatch(createPromoCode(input as Omit<PromoCode, 'id' | 'usageCount' | 'createdAt' | 'status'>));
        }
        closeForm();
    }

    function editHandler(id: string) {
        dispatch(selectPromoCode(id));
        openForm();
    }

    function deleteHandler(id: string) {
        const promo = promoCodes.find((p) => p.id === id);
        if (promo && promo.usageCount > 0) {
            dispatch(showAlert({ type: AlertType.Error, message: DELETE_PROMOCODE_ALERT_MESSAGE }));
            return;
        }
        dispatch(deletePromoCode(id));
    }

    const isPlaceholderVisible = !isLoading && !error.isError && !isFormOpen && promoCodes.length === 0;

    return (
        <Card>
            <div className={classes.promocodes}>
                <div className={classes.header}>
                    <h3 className={classes.title}>Промо-коды</h3>
                    <IconButton onClick={openForm} isDisabled={isFormOpen || error.isError}>
                        <AddIcon />
                    </IconButton>
                </div>
                {isLoading && <AreaLoader />}
                {!isLoading && error.isError && !isFormOpen && <Placeholder text={error.message} />}
                {isPlaceholderVisible && <Placeholder text={NO_PROMOCODES_MESSAGE} />}
                {isFormOpen && (
                    <PromoCodeForm
                        value={input}
                        errors={errors}
                        onInputChange={handleInputChange}
                        onSelect={handleSelect}
                        onClose={closeForm}
                        onSubmit={submit}
                        isEditMode={!!selectedPromoCode.id}
                    />
                )}
                {!isFormOpen && promoCodes.length > 0 && <PromoCodesList items={promoCodes} onEdit={editHandler} onDelete={deleteHandler} />}
            </div>
        </Card>
    );
};

export default PromoCodes;
